import { ArrowLeftIcon, InfoIcon, ListPlusIcon, PlusIcon, TriangleAlertIcon } from "lucide-react"
import type * as React from "react"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, useNavigate, useParams } from "react-router-dom"
import { toast } from "sonner"
import { ItemPicker } from "@/features/invoices/DocumentPickers"
import { LineCard, TotalsBar } from "@/features/invoices/InvoiceEditorScreen"
import {
  creditNoteLinesFromInvoice,
  emptyLine,
  makeKeyFactory,
  text,
  usableApiLines,
  type EditorLine,
} from "@/features/invoices/use-invoice-editor"
import { useInvoice } from "@/features/invoices/use-invoice"
import { useIssueCreditNote } from "@/features/credit-notes/use-credit-note"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { DateField } from "@/components/DateField"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import { Textarea } from "@/components/ui/textarea"
import { requestBlob } from "@/lib/api"
import { ApiError } from "@/lib/api-error"
import { todayInputValue, useFormatDay } from "@/lib/dates"
import { endpoints, type CreditNoteDetail, type InvoiceDetail } from "@/lib/endpoints"
import { formatRupees } from "@/lib/money"
import { contextForSupplyType, engineLinesFrom, useLiveTotals } from "@/lib/totals"
import { routes } from "@/routes/paths"

/**
 * Raising a credit note against ONE bill (§17.3, section 34(1) CGST).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * IT CANNOT BE REACHED WITHOUT A BILL, AND THAT IS THE FIRST RULE OF THE
 * FEATURE.
 *
 * The path is `/invoices/:id/credit-note`. There is no `/credit-notes/new`, no
 * "which invoice?" picker and no way to arrive here with the field blank —
 * because §34 requires the link, and a form that ASKS which bill is being
 * reduced is a form on which the wrong bill can be chosen. The invoice is not an
 * option on this screen; it is the subject of it, printed at the top, frozen.
 *
 * IT STARTS FROM THE BILL'S OWN LINES so the owner reduces or removes what is
 * actually there, rather than retyping a description that has to match. The
 * seeding goes through `linesFromInvoice` — the SAME mapping "Make a copy" and
 * "Repeat last bill" use (§11.3) — so a line round-trips exactly, and there is
 * no fourth copy of the integer→string scaling to drift.
 *
 * THE LINE EDITOR IS THE SHARED ONE. `LineCard` and `ItemPicker` are the
 * invoice's, unchanged; `TotalsBar` is the invoice's bottom bar, widened on
 * 2026-08-16 to take figures rather than a preview query precisely so this
 * screen did not need a fourth.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT THIS SCREEN DOES NOT DECIDE, and every one of them is the server's (§6):
 *
 *   THE NUMBER      — `CRN/26-27/n`, allocated inside the write transaction
 *                     under the company-row lock, gap-free (§5).
 *   THE BUYER       — read off the LOCKED invoice. Not sent, not editable, and
 *                     not even a field: a credit note cannot be addressed to
 *                     anyone but the buyer who was billed.
 *   THE SPLIT       — `supplyType` is COPIED from the original (§8.7). A buyer
 *                     who has moved state since must not flip CGST+SGST into
 *                     IGST here, or the original liability stays un-reversed in
 *                     two heads while a new one is created in a third.
 *   THE RATES       — resolved as of the ORIGINAL INVOICE'S date, not today's.
 *                     Crediting HSN 7308 against a bill that charged 12%
 *                     reverses 12% even if the Council has since moved it.
 *   EVERY TOTAL     — recomputed from the lines.
 *   THE CEILING     — the sum of notes against one bill may not exceed its grand
 *                     total, checked under the invoice row lock. This screen
 *                     PRINTS the bill's total so the owner can see the ceiling;
 *                     it does not enforce it, because a client check would be a
 *                     second opinion that two concurrent notes could both pass.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * THE RUNNING TOTAL IS `@steelbill/tax`, NOT A SECOND TAX ENGINE. There is no
 * `POST /credit-notes/preview` — the JSON one — to call (see
 * `use-credit-note.ts`), so the figure at the foot of this screen comes from
 * `useLiveTotals`: the same `computeTotals` the server runs, imported from the
 * same package, which is exactly what §11.1 describes and what `lib/totals.ts`
 * exists for. Nothing is reimplemented here; the server still recomputes and is
 * still the authority.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE **SHEET** IS A DIFFERENT QUESTION, AND SINCE 2026-08-17 IT HAS AN ANSWER.
 * `POST /credit-notes/preview-pdf` renders this unsaved form as the paper it
 * would be, watermarked DRAFT, and the owner asked for it because this document
 * has no draft state to fall back on: saving ISSUES it, with a number out of a
 * series s.34(1) requires to run unbroken. The figures on that sheet are the
 * SERVER's — recomputed at the original bill's rates and in the original's split
 * — which is precisely what the browser above cannot know. Nothing about the
 * running total changed; a sheet was added beside it. See {@link onPreviewPdf}.
 * ─────────────────────────────────────────────────────────────────────────────
 * ═════════════════════════════════════════════════════════════════════════════
 */

interface CreditNoteEditorState {
  /** The NOTE's own date — today, normally. Not the invoice's (§34). */
  date: string
  /** §34(1) — mandatory. The one field with no counterpart on a bill. */
  reason: string
  attnName: string
  notes: string
  lines: EditorLine[]
  /**
   * Did the bill being reduced carry a §15(3)(a) discount, so that at least one
   * seeded line shows what was CHARGED rather than what was quoted?
   *
   * Only a banner depends on it. The figures are already right without it — the
   * seeding put them right — but a line whose rate no longer matches the rate on
   * the paper bill in his hand is exactly the thing the owner would otherwise
   * read as the app having got it wrong.
   */
  seededAfterDiscount: boolean
}

/** Line keys for this editor. `C` so a key from a copied bill cannot collide. */
const keys = makeKeyFactory("C")

export function CreditNoteEditorScreen(): React.JSX.Element {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const invoiceQuery = useInvoice(id)
  const invoice = invoiceQuery.data
  const issue = useIssueCreditNote()

  const [state, setState] = useState<CreditNoteEditorState>({
    date: todayInputValue(),
    reason: "",
    attnName: "",
    notes: "",
    lines: [],
    seededAfterDiscount: false,
  })
  const [seeded, setSeeded] = useState(false)
  const [pickingItemFor, setPickingItemFor] = useState<string | null>(null)
  /*
   * DECLARED UP HERE WITH THE OTHER STATE, above the three early returns below.
   * A `useState` after `if (invoiceQuery.isPending) return …` is a hook that
   * sometimes runs and sometimes does not, which React refuses.
   */
  const [previewing, setPreviewing] = useState(false)

  /*
   * SEED ONCE, AND ONLY ONCE. `useInvoice` refetches — the sync button on the
   * bill's own screen shares this cache — and re-seeding on every settled query
   * would silently throw away lines the owner had already removed. `seeded`
   * rather than a dependency on `invoice`, because the object identity changes
   * on a refetch even when nothing in it did.
   */
  useEffect(() => {
    if (seeded || invoice === undefined) return
    /*
     * `creditNoteLinesFromInvoice`, NOT the shared `linesFromInvoice` the copy
     * paths use, and the difference is §15(3)(a).
     *
     * A credit note carries no discount of either kind — `CreditNoteCreateSchema`
     * has no document field and its lines are `LinesSchema`, which strips
     * `discountPaise`. So a seed that copied the invoice's gross rates would
     * reverse the bill AS IT WAS BEFORE the discount: more value and more output
     * tax than was ever charged. This mapping seeds a discounted line at the
     * value of supply the invoice actually taxed, and leaves an undiscounted one
     * exactly as it was.
     */
    const restored = creditNoteLinesFromInvoice(invoice, keys.next)
    setState((current) => ({
      ...current,
      attnName: restored.attnName,
      lines: restored.lines,
      seededAfterDiscount: restored.anyDiscount,
    }))
    setSeeded(true)
  }, [invoice, seeded])

  const patch = (changes: Partial<CreditNoteEditorState>) => {
    setState((current) => ({ ...current, ...changes }))
  }

  const patchLine = (key: string, changes: Partial<EditorLine>) => {
    setState((current) => ({
      ...current,
      lines: current.lines.map((line) => (line.key === key ? { ...line, ...changes } : line)),
    }))
  }

  const addLine = (parentKey: string | null = null) => {
    setState((current) => ({
      ...current,
      lines: [...current.lines, { ...emptyLine(keys.next()), parentKey }],
    }))
  }

  const addGroup = () => {
    const headerKey = keys.next()
    setState((current) => ({
      ...current,
      lines: [
        ...current.lines,
        { ...emptyLine(headerKey), isGroupHeader: true },
        { ...emptyLine(keys.next()), parentKey: headerKey },
      ],
    }))
  }

  const removeLine = (key: string) => {
    setState((current) => ({
      // A header takes its children with it — §4 allows two levels, and an
      // orphaned child would be a measured line with no heading above it.
      ...current,
      lines: current.lines.filter((line) => line.key !== key && line.parentKey !== key),
    }))
  }

  const apiLines = usableApiLines(state.lines)

  /*
   * THE SPLIT IS THE ORIGINAL'S, COPIED — never re-derived from the buyer.
   *
   * `contextForSupplyType` takes the invoice's OWN `supplyType`, a field the
   * SERVER computed, and expresses it in the two-code shape the engine's
   * signature asks for. The engine reads nothing from those codes but whether
   * they are equal, so this cannot disagree with the server: it is copying an
   * answer rather than working one out. §8.7.
   */
  const context = useMemo(
    () =>
      invoice === undefined
        ? null
        : contextForSupplyType(invoice.supplyType, invoice.placeOfSupply),
    [invoice],
  )

  const engineLines = useMemo(
    () => (apiLines === null ? [] : engineLinesFrom(apiLines)),
    [apiLines],
  )
  const live = useLiveTotals(engineLines, context)

  /*
   * The shared bar's shape, filled from the engine instead of a preview query.
   * `isError: false` because there is no request to fail — an incomputable
   * document shows the bar's own "not complete yet" line, which is what a
   * half-typed line means.
   */
  const figures = useMemo(
    () =>
      live.result === null
        ? { data: undefined, isError: false, error: null }
        : {
            data: {
              data: {
                supplyType: live.result.totals.supplyType,
                totals: {
                  subtotalPaise: live.result.totals.subtotalPaise,
                  cgstPaise: live.result.totals.cgstPaise,
                  sgstPaise: live.result.totals.sgstPaise,
                  igstPaise: live.result.totals.igstPaise,
                  totalTaxPaise: live.result.totals.totalTaxPaise,
                  roundOffPaise: live.result.totals.roundOffPaise,
                  grandTotalPaise: live.result.totals.grandTotalPaise,
                  /*
                   * ALL ZERO, AND NOW STRUCTURALLY SO — which it was not when
                   * this note was first written.
                   *
                   * The claim used to be that "`CreditNoteIssueSchema` carries no
                   * discount field for the editor to send". No schema of that
                   * name exists; the real one is `CreditNoteCreateSchema`, whose
                   * lines are `LinesSchema`, and the editor DID send
                   * `discountPaise` — seeded off the bill by `linesFromInvoice`,
                   * emitted by `toApiLine`, applied by `computeTotals` here and
                   * silently stripped by `z.object()` there. So these four were
                   * not zero, the bar printed "Before discount … · Discount given
                   * …" on a credit note, and the issued document carried neither.
                   *
                   * What makes them zero is upstream and deliberate:
                   * `creditNoteLinesFromInvoice` seeds every line with an empty
                   * discount box, and `LineCard` is rendered with
                   * `discountable={false}` so none can be typed. They are still
                   * read off the engine rather than written as literals, so this
                   * stays a fact about the request rather than a claim made here.
                   */
                  grossSubtotalPaise: live.result.totals.grossSubtotalPaise,
                  lineDiscountTotalPaise: live.result.totals.lineDiscountTotalPaise,
                  documentDiscountPaise: live.result.totals.documentDiscountPaise,
                  /*
                   * §15(3)(a) PROVENANCE, AND `null` IS A LITERAL HERE FOR ONCE.
                   *
                   * Unlike the four figures around it this one has no engine
                   * source to read: `computeTotals` takes paise and only paise
                   * and has never seen a percentage. `null` is the honest value
                   * and the only reachable one — a credit note carries no
                   * discount of either kind, so there is no figure for a
                   * percentage to be the provenance of.
                   */
                  documentDiscountPercentBp: null,
                  totalDiscountPaise: live.result.totals.totalDiscountPaise,
                  /* Worded by the SERVER on the saved document (§6). Nothing on
                   * an editor may put a number into words — the bar does not
                   * read this, and inventing a client wording is how the screen
                   * and the printed sheet end up saying different things. */
                  amountInWords: "",
                },
              },
            },
            isError: false,
            error: null,
          },
    [live.result],
  )

  if (invoiceQuery.isPending) return <EditorSkeleton />

  if (invoiceQuery.isError || invoice === undefined) {
    const notFound = invoiceQuery.error instanceof ApiError && invoiceQuery.error.status === 404
    return (
      <div className="flex flex-col gap-4 p-4 md:p-6">
        <BackLink invoiceId={id} />
        <Alert variant="destructive">
          <AlertTitle>{notFound ? t("err.notFound") : t("err.couldNotLoad")}</AlertTitle>
          <AlertDescription>
            {invoiceQuery.error instanceof ApiError
              ? invoiceQuery.error.message
              : t("err.somethingWentWrong")}
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  /*
   * RULE 4 — THE ORIGINAL MUST BE ISSUED, refused HERE as well as on the server.
   *
   * The API answers 409 naming which, under the invoice row lock, and that is
   * the authority. This is not a second gate: it is the screen declining to open
   * an editor whose save cannot succeed, and it names the same two reasons in
   * the same words. A DRAFT has billed nothing — there is no liability to
   * reduce, and the fix is to finish or discard the draft. A CANCELLED bill has
   * been withdrawn in full already, so crediting it would reduce a document that
   * no longer stands.
   */
  if (invoice.status !== "ISSUED") {
    return (
      <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
        <BackLink invoiceId={invoice.id} />
        <Alert variant="destructive">
          <TriangleAlertIcon />
          <AlertTitle>{t("creditNote.notCreditableTitle", { number: invoice.number })}</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-3">
            <span>
              {invoice.status === "CANCELLED"
                ? t("creditNote.notCreditableCancelled", { number: invoice.number })
                : t("creditNote.notCreditableDraft", { number: invoice.number })}
            </span>
            <Button
              variant="outline"
              nativeButton={false}
              render={<Link to={routes.invoice(invoice.id)} />}
            >
              {t("creditNote.backToBill", { number: invoice.number })}
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  const reason = state.reason.trim()
  const canSave =
    apiLines !== null && reason !== "" && state.date.trim() !== "" && !issue.isPending

  /*
   * ONE BODY, BUILT ONCE, FOR BOTH BUTTONS — the challan editor's
   * `toChallanApiBody` shape, and it is the whole point of building it here.
   *
   * The preview route validates `CreditNoteCreateSchema` VERBATIM, i.e. the very
   * schema the save posts to, so the sheet the owner checks describes the
   * request the save will send — down to the trimmed reason and the emptied
   * fields that must travel as null rather than "". If Preview composed its own
   * object the two would differ the first time either was touched, and the
   * difference would be invisible: he would approve one document and issue
   * another, on the one document that cannot be edited afterwards (§17.3).
   *
   * `null` MEANS NOT YET SENDABLE, on exactly the same conditions as `canSave`.
   */
  const body =
    apiLines === null || reason === ""
      ? null
      : {
          invoiceId: invoice.id,
          date: state.date,
          reason,
          attnName: text(state.attnName),
          notes: text(state.notes),
          lines: apiLines,
        }

  /*
   * PREVIEW — the unsaved credit note as the PAPER IT WOULD BE, DRAFT-stamped
   * corner to corner.
   *
   * ═══════════════════════════════════════════════════════════════════════════
   * THIS EDITOR WANTS IT MORE THAN THE OTHER THREE, AND THE REASON IS THAT THIS
   * DOCUMENT HAS NO DRAFT. An invoice, a quotation and a challan can all be
   * saved and looked at again; a credit note is ISSUED the moment it is saved —
   * it takes a number out of a series s.34(1) requires to run unbroken, it is
   * declared in that month's GSTR-1, there is no edit path and no
   * `creditnote.edit` capability for anyone (§2.1), and the only way back is to
   * cancel it and raise another. So the last look belongs right next to the
   * button that spends the number, which is where `TotalsBar` puts it.
   *
   * NOTHING HERE COMPUTES OR SENDS A FIGURE. The server recomputes every amount
   * from these lines, at the ORIGINAL BILL'S rates and in the ORIGINAL's split
   * (§6, §8.7) — which is exactly what the browser cannot know and therefore
   * exactly what makes the sheet worth looking at.
   *
   * NO `?draft=` AND NO `?editing=`, unlike the challan's and the quotation's.
   * There are no credit-note drafts to name and no saved note this form could be
   * replacing, so the number box prints plain DRAFT — never a predicted
   * `CRN/26-27/n`, which is allocated inside the save transaction (§5, §6).
   *
   * DISABLED, NOT HIDDEN, until the form could be saved: `canSave` is the same
   * rule that disables Save beside it, and the totals bar already says in words
   * why the document is not complete. The owner's "hide, do not disable" rule is
   * about controls that do not APPLY to a document; Preview applies to every
   * credit note and is momentarily not YET usable.
   * ═══════════════════════════════════════════════════════════════════════════
   */
  const onPreviewPdf = async () => {
    if (body === null) return
    setPreviewing(true)
    try {
      const fetched = await requestBlob(endpoints.creditNotePreviewPdf, {
        body,
        fallbackFilename: "draft-preview.pdf",
      })
      const url = URL.createObjectURL(fetched.blob)
      // A NEW TAB: the half-typed form must survive the look.
      window.open(url, "_blank", "noopener")
      window.setTimeout(() => {
        URL.revokeObjectURL(url)
      }, 60_000)
    } catch (error) {
      /*
       * THE SERVER'S SENTENCE, VERBATIM — the same treatment the issue error
       * gets below. A 409 from here is information ("that bill was cancelled"),
       * not a transient failure, and the message already names which.
       */
      toast.error(error instanceof ApiError ? error.message : t("err.somethingWentWrong"))
    } finally {
      setPreviewing(false)
    }
  }

  const save = () => {
    if (body === null) return
    issue.mutate(
      body,
      {
        onSuccess: (response) => {
          const note: CreditNoteDetail = response.data
          /*
           * §34(2)'s DEADLINE ARRIVES AS A `warnings` ENTRY ON A 201, and it
           * must survive the navigation — a toast on a screen that is being
           * replaced is a sentence nobody reads. So it rides in the router's
           * location state and the detail screen renders it in a banner.
           *
           * The warning does NOT mean the save failed. The note exists and holds
           * its number; what may be lost is the tax adjustment. Nothing here
           * re-derives that date — the server owns the rule (Asia/Kolkata, the
           * financial year of the SUPPLY, not of the note).
           */
          void navigate(routes.creditNote(note.id), {
            replace: true,
            state: { warnings: response.warnings ?? [] },
          })
        },
      },
    )
  }

  return (
    <div className="flex flex-col gap-4 p-4 pb-40 md:gap-6 md:p-6 md:pb-40">
      <BackLink invoiceId={invoice.id} />

      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold tracking-tight">{t("creditNote.newTitle")}</h1>
        <p className="text-muted-foreground max-w-2xl text-sm">{t("creditNote.newSubtitle")}</p>
      </header>

      {/*
        * §34's LINK, AT THE TOP, AND READ-ONLY. The bill being reduced is the
        * first fact on the screen because it is the one fact that cannot be
        * changed here — there is no picker and no field, only what was opened
        * from. The grand total beside it is the CEILING (rule 3): the sum of
        * every credit note against this bill may not exceed it, and the server
        * refuses the excess under the invoice row lock.
        */}
      <OriginalBillCard invoice={invoice} />

      <Card>
        <CardHeader>
          <CardTitle>{t("creditNote.whyTitle")}</CardTitle>
          <CardDescription>{t("creditNote.whyHelp")}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="credit-note-reason">{t("creditNote.reasonLabel")}</Label>
            <Textarea
              id="credit-note-reason"
              rows={2}
              maxLength={500}
              value={state.reason}
              placeholder={t("creditNote.reasonPlaceholder")}
              onChange={(event) => {
                patch({ reason: event.target.value })
              }}
            />
            <p className="text-muted-foreground text-xs">{t("creditNote.reasonHelp")}</p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="credit-note-date">{t("creditNote.dateLabel")}</Label>
              <DateField
                id="credit-note-date"
                value={state.date}
                onChange={(value) => {
                  patch({ date: value })
                }}
              />
              {/* It is NOT the invoice's date: this document is issued now, takes
                * its number from the series current now, and is declared in THIS
                * month's GSTR-1. The tax it reverses is still the tax that was
                * charged, which the server resolves from the original's date. */}
              <p className="text-muted-foreground text-xs">{t("creditNote.dateHelp")}</p>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="credit-note-attn">{t("lbl.contactName")}</Label>
              <Input
                id="credit-note-attn"
                value={state.attnName}
                onChange={(event) => {
                  patch({ attnName: event.target.value })
                }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("creditNote.linesTitle")}</CardTitle>
          <CardDescription>{t("creditNote.linesHelp")}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {/*
            * SAID BEFORE THE FIGURES, NOT AFTER — the same placement the challan
            * editor uses for the opposite sentence. Every amount on this screen
            * is POSITIVE and stays positive on the saved document (§17.3); what
            * makes it a reduction is the document, not a minus sign.
            */}
          <p className="text-muted-foreground rounded-md border border-dashed p-3 text-xs">
            {t("creditNote.positiveAmounts")}
          </p>

          {/*
            * SAID ONLY WHEN A LINE WAS ACTUALLY REWRITTEN. The rate on a
            * discounted line is not the rate printed on the bill in his hand —
            * it is what that line was charged AFTER the discount — and an
            * unexplained smaller number reads as the app having lost money.
            */}
          {state.seededAfterDiscount ? (
            <Alert>
              <InfoIcon />
              <AlertDescription>{t("creditNote.linesAfterDiscount")}</AlertDescription>
            </Alert>
          ) : null}

          {state.lines.map((line) => (
            <LineCard
              key={line.key}
              line={line}
              lines={state.lines}
              /*
               * NO DISCOUNT BOX. §15(3)(a)'s scope is invoices and quotations; a
               * credit note is already a reduction, and its lines are validated
               * by `LinesSchema`, which has no `discountPaise` at all. The box
               * was on this screen, seeded from the bill, and the client's own
               * engine APPLIED what it collected while `z.object()` stripped it
               * from the request — so the bar read one total and the issued note
               * carried another, on a document that is issued immediately, spends
               * its number, and cannot be edited afterwards.
               */
              discountable={false}
              /*
               * The ENGINE's figure for this line, not a server preview's —
               * there is no JSON preview route for this kind, and the PDF one
               * returns a document rather than per-line numbers. `lineCode` is
               * empty because §4's SR.NO hierarchy is derived server-side when
               * the document is written; the column is blank here exactly as it
               * is in every other editor before its first preview lands.
               */
              preview={(() => {
                const computed = live.result?.lines.find((row) => row.key === line.key)
                return computed === undefined
                  ? undefined
                  : { taxablePaise: computed.taxablePaise, lineCode: "" }
              })()}
              onChange={(changes) => {
                patchLine(line.key, changes)
              }}
              onRemove={() => {
                removeLine(line.key)
              }}
              onPickItem={() => {
                setPickingItemFor(line.key)
              }}
              onAddChild={() => {
                addLine(line.key)
              }}
            />
          ))}

          {state.lines.length === 0 ? (
            <Alert>
              <InfoIcon />
              <AlertTitle>{t("creditNote.noLinesTitle")}</AlertTitle>
              <AlertDescription>{t("creditNote.noLinesHelp")}</AlertDescription>
            </Alert>
          ) : null}

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                addLine()
              }}
            >
              <PlusIcon />
              {t("btn.addLine")}
            </Button>
            <Button type="button" variant="outline" onClick={addGroup}>
              <ListPlusIcon />
              {t("btn.addSubLine")}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("lbl.notes")}</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            aria-label={t("lbl.notes")}
            rows={3}
            value={state.notes}
            onChange={(event) => {
              patch({ notes: event.target.value })
            }}
          />
          {/*
            * NO TERMS FIELD, AND NOT AN OVERSIGHT. `credit_notes` has no `terms`
            * column: standing terms are an offer about future work, and a
            * document reducing a bill already issued has nothing to offer. The
            * PDF passes `termsFallback: false` for the same reason, so the
            * company's default terms do not print on the sheet either.
            */}
        </CardContent>
      </Card>

      {issue.isError ? (
        <Alert variant="destructive">
          <TriangleAlertIcon />
          <AlertTitle>{t("creditNote.couldNotIssueTitle")}</AlertTitle>
          <AlertDescription>
            {/*
              * THE SERVER'S SENTENCE, VERBATIM. A 409 here is information, not a
              * transient failure: the original is a draft or cancelled, or the
              * notes against this bill would exceed it — and the message already
              * names which, with the amount Indian-grouped. Composing a second
              * sentence over it would say less and could disagree.
              */}
            {issue.error instanceof ApiError ? issue.error.message : t("err.somethingWentWrong")}
          </AlertDescription>
        </Alert>
      ) : null}

      {/*
        * `onPreviewPdf` IS WHAT SHOWS THE PREVIEW BUTTON — the same optional
        * prop the other three editors pass, so this screen gets the identical
        * control in the identical place beside "Make the credit note", with the
        * identical wording (`btn.previewPdf`) and spinner.
        *
        * NO `emphasiseSubtotal`: that is §9.3's quotation-only rule about which
        * figure a quotation SHEET prints as its headline. A credit note's
        * headline is the tax-inclusive TOTAL CREDIT, which is what comes off the
        * bill.
        */}
      <TotalsBar
        preview={figures}
        canSave={canSave}
        saving={issue.isPending}
        incomplete={apiLines === null}
        onSave={save}
        previewing={previewing}
        onPreviewPdf={() => {
          void onPreviewPdf()
        }}
        saveLabel={t("creditNote.issueAction")}
      />

      <ItemPicker
        open={pickingItemFor !== null}
        onOpenChange={(open) => {
          if (!open) setPickingItemFor(null)
        }}
        onPick={(item) => {
          if (pickingItemFor === null) return
          patchLine(pickingItemFor, {
            itemId: item.id,
            description: item.name,
            supplyNature: item.supplyNature as EditorLine["supplyNature"],
            hsn: item.hsn,
            gstRatePercent: String(item.gstRateBp / 100),
            pricingMode: item.pricingMode as EditorLine["pricingMode"],
            unitLabel: item.unitLabel,
            rate: String(item.ratePaise / 100),
          })
          setPickingItemFor(null)
        }}
      />

      <p className="text-muted-foreground max-w-2xl text-xs">{t("creditNote.serverDecides")}</p>
      {/* Said once, next to the sentence about what the server decides: the
        * preview costs nothing and the save cannot be taken back. */}
      <p className="text-muted-foreground max-w-2xl text-xs">{t("creditNote.previewHelp")}</p>
    </div>
  )
}

/**
 * The bill this note reduces — §34's link, shown before anything else.
 *
 * THE GRAND TOTAL IS THE CEILING, and it is printed rather than enforced here.
 * Rule 3 bounds the SUM of every note against this invoice, which only the
 * server can evaluate — it does so with the invoice row locked, so two notes
 * that would jointly exceed the bill cannot both land. A client-side check would
 * be a second opinion that both of them would pass.
 *
 * A PAID BILL IS STILL CREDITABLE. §34 says nothing about payment, and the cap
 * is against the BILL rather than the unpaid part of it — so the figure here is
 * the grand total and never an outstanding balance.
 */
function OriginalBillCard({ invoice }: { invoice: InvoiceDetail }): React.JSX.Element {
  const { t } = useTranslation()
  const formatDay = useFormatDay()

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("creditNote.againstTitle")}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 text-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-col">
            <Link
              to={routes.invoice(invoice.id)}
              className="hover:text-primary font-medium tabular-nums underline-offset-4 hover:underline"
            >
              {invoice.number}
            </Link>
            <span className="text-muted-foreground">
              {/* The FROZEN snapshot (§4) — who was billed that day. The credit
                * note copies this same buyer off the invoice; it is never read
                * from the customer row as it stands today (§8.7). */}
              {invoice.customer?.name ?? t("lbl.customerUnknown")} · {formatDay(invoice.date)}
            </span>
          </div>
          <div className="flex flex-col items-end">
            <span className="text-muted-foreground text-xs">{t("creditNote.billTotalLabel")}</span>
            <span className="text-base font-semibold tabular-nums">
              {formatRupees(invoice.totals.grandTotalPaise)}
            </span>
          </div>
        </div>
        <p className="text-muted-foreground text-xs">{t("creditNote.ceilingHelp")}</p>
      </CardContent>
    </Card>
  )
}

function BackLink({ invoiceId }: { invoiceId: number | string | undefined }): React.JSX.Element {
  const { t } = useTranslation()
  return (
    <Link
      to={invoiceId === undefined ? routes.invoices : routes.invoice(invoiceId)}
      className="text-muted-foreground hover:text-foreground inline-flex w-fit items-center gap-1.5 text-sm"
    >
      <ArrowLeftIcon aria-hidden="true" className="size-4" />
      {t("btn.back")}
    </Link>
  )
}

function EditorSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
      <Skeleton className="h-4 w-20" />
      <Skeleton className="h-8 w-64" />
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-40 w-full" />
      <Skeleton className="h-64 w-full" />
    </div>
  )
}
